import React, { useState, useMemo } from 'react';
import { 
  Gavel, 
  FileText, 
  MessageCircle, 
  Search, 
  Filter, 
  Eye, 
  AlertTriangle, 
  CheckCircle, 
  Plus,
  Clock, 
  UserCheck,
  Scale
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Type Definitions
interface Dispute {
  id: string;
  title: string;
  parties: {
    complainant: string;
    respondent: string;
  };
  status: 'open' | 'in_mediation' | 'resolved' | 'appealed';
  priority: 'low' | 'medium' | 'high';
  openDate: string;
  lastUpdated: string;
  category: string;
  description: string;
}

interface MediationCase {
  id: string;
  disputeId: string;
  mediator: string;
  startDate: string;
  expectedResolutionDate: string;
  status: 'ongoing' | 'completed' | 'failed';
}

interface CommunicationLog {
  id: string;
  disputeId: string;
  timestamp: string;
  sender: string;
  message: string;
  type: 'message' | 'system_note' | 'resolution_update';
}

interface ResolutionTemplate {
  id: string;
  name: string;
  category: string;
  content: string;
  lastUpdated: string;
}

const DisputeResolutionCenter: React.FC = () => {
  // Breadcrumb Management
  useBreadcrumbs([
        { label: 'Escrow Management', href: '#' },
        { label: 'Escrow Dispute Resolution Center' }
      ]);
  // State Management
  const [disputes, setDisputes] = useState<Dispute[]>([
    {
      id: 'DISP001',
      title: 'Product Quality Dispute',
      parties: {
        complainant: 'John Buyer',
        respondent: 'ABC Seller'
      },
      status: 'open',
      priority: 'high',
      openDate: '2024-02-15',
      lastUpdated: '2024-02-20',
      category: 'Product Delivery',
      description: 'Received damaged goods, seeking replacement or refund'
    },
    {
      id: 'DISP002',
      title: 'Service Delay Complaint',
      parties: {
        complainant: 'Jane Service User',
        respondent: 'XYZ Service Provider'
      },
      status: 'in_mediation',
      priority: 'medium',
      openDate: '2024-01-25',
      lastUpdated: '2024-02-18',
      category: 'Service Delivery',
      description: 'Significant delay in service completion'
    }
  ]);

  const [mediationCases, setMediationCases] = useState<MediationCase[]>([
    {
      id: 'MED001',
      disputeId: 'DISP002',
      mediator: 'Sarah Thompson',
      startDate: '2024-02-01',
      expectedResolutionDate: '2024-03-01',
      status: 'ongoing'
    }
  ]);

  const [communicationLogs, setCommunicationLogs] = useState<CommunicationLog[]>([
    {
      id: 'LOG001',
      disputeId: 'DISP001',
      timestamp: '2024-02-20 10:30:45',
      sender: 'System',
      message: 'Dispute opened and assigned to mediation team',
      type: 'system_note'
    }
  ]);

  const [resolutionTemplates, setResolutionTemplates] = useState<ResolutionTemplate[]>([
    {
      id: 'TMPL001',
      name: 'Product Return Resolution',
      category: 'Product Delivery',
      content: 'Standard resolution for product return cases including refund or replacement',
      lastUpdated: '2024-02-10'
    }
  ]);

  // Filtering States
  const [disputeStatusFilter, setDisputeStatusFilter] = useState('all');
  const [disputePriorityFilter, setDisputePriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Selected Item for Modal
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [isDisputeModalOpen, setIsDisputeModalOpen] = useState(false);

  // Filtered Disputes
  const filteredDisputes = useMemo(() => {
    return disputes.filter(dispute => {
      const matchesStatus = disputeStatusFilter === 'all' || dispute.status === disputeStatusFilter;
      const matchesPriority = disputePriorityFilter === 'all' || dispute.priority === disputePriorityFilter;
      const matchesSearch = 
        dispute.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dispute.parties.complainant.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dispute.parties.respondent.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesStatus && matchesPriority && matchesSearch;
    });
  }, [disputes, disputeStatusFilter, disputePriorityFilter, searchQuery]);

  // Status Color Helpers
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800';
      case 'in_mediation': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'appealed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Dispute Details Modal
  const DisputeDetailsModal = () => {
    if (!selectedDispute) return null;

    // Find related mediation case
    const relatedMediation = mediationCases.find(
      medCase => medCase.disputeId === selectedDispute.id
    );

    // Find communication logs for this dispute
    const disputeLogs = communicationLogs.filter(
      log => log.disputeId === selectedDispute.id
    );

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-[500px] max-h-[90vh] overflow-y-auto">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Gavel className="mr-2 h-6 w-6 text-blue-500" />
            Dispute Details
          </h2>
          
          {/* Dispute Basic Information */}
          <div className="space-y-4">
            <div>
              <span className="text-gray-500 block">Title</span>
              <p className="font-semibold">{selectedDispute.title}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-gray-500 block">Complainant</span>
                <p>{selectedDispute.parties.complainant}</p>
              </div>
              <div>
                <span className="text-gray-500 block">Respondent</span>
                <p>{selectedDispute.parties.respondent}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-gray-500 block">Status</span>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedDispute.status)}`}>
                  {selectedDispute.status.replace('_', ' ').charAt(0).toUpperCase() + selectedDispute.status.replace('_', ' ').slice(1)}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Priority</span>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(selectedDispute.priority)}`}>
                  {selectedDispute.priority.charAt(0).toUpperCase() + selectedDispute.priority.slice(1)}
                </span>
              </div>
            </div>
            
            <div>
              <span className="text-gray-500 block">Description</span>
              <p>{selectedDispute.description}</p>
            </div>
            
            {/* Mediation Information */}
            {relatedMediation && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <Scale className="mr-2 h-5 w-5 text-green-500" />
                  Mediation Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-500 block">Mediator</span>
                    <p>{relatedMediation.mediator}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Status</span>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      relatedMediation.status === 'ongoing'
                        ? 'bg-yellow-100 text-yellow-800'
                        : relatedMediation.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {relatedMediation.status.charAt(0).toUpperCase() + relatedMediation.status.slice(1)}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <span className="text-gray-500 block">Start Date</span>
                    <p>{relatedMediation.startDate}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Expected Resolution</span>
                    <p>{relatedMediation.expectedResolutionDate}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Communication Logs */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-2 flex items-center">
                <MessageCircle className="mr-2 h-5 w-5 text-blue-500" />
                Communication Logs
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {disputeLogs.map((log) => (
                  <div 
                    key={log.id} 
                    className={`p-2 rounded-lg ${
                      log.type === 'system_note' 
                        ? 'bg-gray-100' 
                        : log.type === 'message' 
                        ? 'bg-blue-50' 
                        : 'bg-green-50'
                    }`}
                  >
                    <div className="flex justify-between">
                      <span className="font-medium">{log.sender}</span>
                      <span className="text-xs text-gray-500">{log.timestamp}</span>
                    </div>
                    <p className="text-sm">{log.message}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Modal Actions */}
          <div className="mt-6 flex justify-end space-x-2">
            <button 
              onClick={() => setIsDisputeModalOpen(false)}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Gavel className="mr-3 h-8 w-8 text-blue-600" />
            Dispute Resolution Center
          </h1>

          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-wrap items-center space-x-4 space-y-2">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder="Search disputes, parties..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              </div>
              
              <select
                value={disputeStatusFilter}
                onChange={(e) => setDisputeStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="open">Open</option>
                <option value="in_mediation">In Mediation</option>
                <option value="resolved">Resolved</option>
                <option value="appealed">Appealed</option>
              </select>
              
              <select
                value={disputePriorityFilter}
                onChange={(e) => setDisputePriorityFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          {/* Disputes Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dispute Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500uppercase tracking-wider">
                      Parties
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Open Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDisputes.map((dispute) => (
                    <tr key={dispute.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {dispute.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>
                          <p className="font-medium">Complainant: {dispute.parties.complainant}</p>
                          <p className="text-xs text-gray-500">Respondent: {dispute.parties.respondent}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {dispute.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(dispute.status)}`}>
                          {dispute.status.replace('_', ' ').charAt(0).toUpperCase() + dispute.status.replace('_', ' ').slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(dispute.priority)}`}>
                          {dispute.priority.charAt(0).toUpperCase() + dispute.priority.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {dispute.openDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button 
                          onClick={() => {
                            setSelectedDispute(dispute);
                            setIsDisputeModalOpen(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 flex items-center justify-end"
                        >
                          <Eye className="mr-1 h-4 w-4" /> View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Dispute Summary Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Disputes</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {disputes.length}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Open Disputes</p>
                  <p className="text-2xl font-bold text-red-600">
                    {disputes.filter(d => d.status === 'open').length}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Resolved Disputes</p>
                  <p className="text-2xl font-bold text-green-600">
                    {disputes.filter(d => d.status === 'resolved').length}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </div>
          </div>

          {/* Resolution Templates Section */}
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="mr-2 h-5 w-5 text-blue-500" /> 
                Resolution Templates
              </h2>
              <button className="text-blue-600 hover:text-blue-800 flex items-center">
                <Plus className="mr-1 h-4 w-4" /> New Template
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {resolutionTemplates.map((template) => (
                <div 
                  key={template.id} 
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:bg-gray-100"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {template.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-2">
                    Category: {template.category}
                  </p>
                  <p className="text-xs text-gray-500">
                    Last Updated: {template.lastUpdated}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Modals */}
          {isDisputeModalOpen && selectedDispute && (
            <DisputeDetailsModal />
          )}
        </div>
      </div>
    </div>
  );
};

export default DisputeResolutionCenter;